/**
 * Dashboard Metric Snapshot API (CR-4751).
 *
 * Read-only endpoints that serve cached KPI values captured nightly by the
 * backend Celery task. No live computation happens here.
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';

export type PeriodType = 'daily' | 'weekly' | 'monthly' | 'quarterly';

export type MetricType =
  | 'open_order_value'
  | 'total_spend'
  | 'monthly_spend'
  | 'arrears_value'
  | 'arrears_count'
  | 'arrears_opening_value'
  | 'arrears_new_value'
  | 'arrears_resolved_value'
  | 'arrears_closing_value'
  | 'otif_percentage'
  | 'otd_percentage'
  | 'on_time_count'
  | 'early_count'
  | 'late_count'
  | 'total_grns'
  | 'ppm'
  | 'ncr_count'
  | 'ncr_open_count'
  | 'active_supplier_count'
  | 'appeals_count';

export type MetricUnit = 'currency' | 'percentage' | 'count' | 'ppm';

/**
 * CR-4751: metrics a single day does not measure — OTIF and OTD divide by too
 * little in one day to mean anything, and a day has too few receipts for an
 * active-supplier count. The nightly capture writes no daily row for them and
 * the Daily view hides them.
 */
export const DAILY_HIDDEN_METRICS: MetricType[] = [
  'otif_percentage', 'otd_percentage', 'active_supplier_count',
];

/**
 * Metrics that are a balance at a moment (no period of their own). Every other
 * metric measures its period, and has no row for a period with nothing to
 * measure (CR-4751) — the tab shows that as N/A rather than an older value.
 */
export const STATE_METRICS: MetricType[] = [
  'open_order_value', 'arrears_value', 'arrears_count', 'ncr_open_count',
];

export interface DashboardSnapshot {
  id: number;
  company: number | null;
  company_name: string | null;
  group: number | null;
  group_name: string | null;
  snapshot_date: string;
  /** CR-4751: first and last day of the period the row measures (its "as of"). */
  window_start: string;
  window_end: string;
  period_type: PeriodType;
  period_start: string | null;
  period_end: string | null;
  metric_type: MetricType;
  metric_unit: MetricUnit;
  metric_value: string;
  /** CR-4751: the currency `metric_value` is in (money only) — the one asked for, or as held when no rate. */
  currency?: string | null;
  fx_rate_to_base: string | null;
  fx_captured_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface TrendPoint {
  /** The capture date — September's monthly point is stamped 1 October. */
  snapshot_date: string;
  /** CR-4751: first and last day of the period the point measures; label by these. */
  window_start: string;
  window_end: string;
  metric_value: string;
  /** CR-4751: the currency `metric_value` is in (money only). */
  currency?: string | null;
  fx_rate_to_base: string | null;
}

export interface ComparePeriod {
  label: 'current' | 'previous';
  snapshot_date: string | null;
  metric_value: string | null;
  /** CR-4751: the currency `metric_value` is in (money only). */
  currency?: string | null;
  period_start: string | null;
  period_end: string | null;
}

export interface SnapshotListParams {
  metric_type?: MetricType;
  period_type?: PeriodType;
  snapshot_date_from?: string;
  snapshot_date_to?: string;
  company?: number;
  group?: number;
  site?: number;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface TrendsParams {
  metric_type: MetricType;
  period_type?: PeriodType;
  date_from?: string;
  date_to?: string;
  group?: number;
  site?: number;
}

/**
 * CR-4751: the dashboard's site / company / group selection
 * (`useView().getViewQueryParams()`), sent on every call so the figures follow
 * the switcher — the tab used to rely on the scope saved on the profile and
 * kept the previous scope's numbers under the new heading.
 */
export type ScopeParams = Record<string, number | undefined>;

export interface TrendsManyParams {
  metric_types: MetricType[];
  scope?: ScopeParams;
  period_type?: PeriodType;
  date_from?: string;
  date_to?: string;
  /** CR-4751: show money in this currency (the viewer's display currency). */
  currency?: string;
}

export interface CompareParams {
  metric_type: MetricType;
  period_type?: PeriodType;
  current_date?: string;
  /** CR-4751: show money in this currency. */
  currency?: string;
}

export const dashboardSnapshotsAPI = {
  list: (
    params?: SnapshotListParams,
  ): Promise<AxiosResponse<PaginatedResponse<DashboardSnapshot>>> =>
    api.get('/purchasing/dashboard-snapshots/', { params }),

  trends: (
    params: TrendsParams,
  ): Promise<AxiosResponse<TrendPoint[]>> =>
    api.get('/purchasing/dashboard-snapshots/trends/', { params }),

  /** CR-4751: every listed metric's series in ONE call (was one call per metric). */
  trendsMany: (
    { metric_types, scope, ...params }: TrendsManyParams,
  ): Promise<AxiosResponse<Partial<Record<MetricType, TrendPoint[]>>>> =>
    api.get('/purchasing/dashboard-snapshots/trends/', {
      params: { ...scope, ...params, metric_types: metric_types.join(',') },
    }),

  latest: (
    period_type: PeriodType = 'daily',
    scope?: ScopeParams,
    currency?: string,
  ): Promise<AxiosResponse<DashboardSnapshot[]>> =>
    api.get('/purchasing/dashboard-snapshots/latest/', {
      params: { ...scope, period_type, ...(currency ? { currency } : {}) },
    }),

  compare: (
    params: CompareParams,
  ): Promise<AxiosResponse<ComparePeriod[]>> =>
    api.get('/purchasing/dashboard-snapshots/compare/', { params }),
};
